import React, { useCallback, useEffect, useState } from 'react';
import { usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/RolePermissionsPage.module.css';

const modules = ['users', 'tasks', 'job_types', 'teams', 'resources', 'resource_types', 'reports', 'permissions', 'availability', 'logs', 'financials'];
const actions = ['view', 'create', 'edit', 'delete'];

const RolePermissionsPage = () => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);
  const { refreshPermissions } = useAuth();
  const { t } = useLocalization();

  const fetchRolePermissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getRolePermissions();
      setPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to load role permissions:', error);
      alert(t('role_permissions_load_failed', 'Failed to load role permissions'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRolePermissions();
  }, [fetchRolePermissions]);

  const togglePermission = (role, moduleName, action) => {
    setPermissions((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [moduleName]: {
          ...prev[role]?.[moduleName],
          [action]: !prev[role]?.[moduleName]?.[action],
        },
      },
    }));
  };

  const saveRole = async (role) => {
    try {
      setSavingRole(role);
      await usersAPI.updateRolePermissions(role, permissions[role] || {});
      await refreshPermissions();
      alert(`${t('role_permissions_saved_prefix', 'Saved permissions for')} ${role}`);
    } catch (error) {
      console.error('Failed to save role permissions:', error);
      alert(t('role_permissions_save_failed', 'Failed to save permissions'));
    } finally {
      setSavingRole(null);
    }
  };

  const moduleLabelMap = {
    users: t('nav_users', 'Users'),
    tasks: t('nav_tasks', 'Tasks'),
    job_types: t('nav_job_type', 'Job Type'),
    teams: t('nav_teams', 'Teams'),
    resources: t('nav_resources', 'Resources'),
    resource_types: t('nav_resource_type', 'Resource Type'),
    reports: t('nav_report', 'Report'),
    permissions: t('nav_roles', 'Roles'),
    availability: t('nav_daily_attendance', 'Daily Attendance'),
    logs: t('nav_logs', 'Logs'),
    financials: t('dashboard_financial_summary', 'Financial Summary'),
  };

  const actionLabelMap = {
    view: t('role_permissions_action_view', 'View'),
    create: t('role_permissions_action_create', 'Create'),
    edit: t('role_permissions_action_edit', 'Edit'),
    delete: t('role_permissions_action_delete', 'Delete'),
  };

  if (loading) {
    return <div className={styles.container}>{t('role_permissions_loading', 'Loading role permissions...')}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('role_permissions_title', 'Role Permissions')}</h1>
        <p>{t('role_permissions_subtitle', 'Admin can control view, create, edit, and delete permissions for each role.')}</p>
      </div>

      {['admin', 'supervisor', 'user'].map((role) => (
        <section key={role} className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>{role.toUpperCase()}</h2>
            <button
              className={styles.saveBtn}
              onClick={() => saveRole(role)}
              disabled={savingRole === role}
            >
              {savingRole === role ? t('common_saving', 'Saving...') : t('common_save', 'Save')}
            </button>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('activities_module', 'Module')}</th>
                  {actions.map((action) => (
                    <th key={action}>{actionLabelMap[action] || action}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((moduleName) => (
                  <tr key={moduleName}>
                    <td>{moduleLabelMap[moduleName] || moduleName}</td>
                    {actions.map((action) => (
                      <td key={`${moduleName}-${action}`}>
                        <input
                          type="checkbox"
                          checked={!!permissions?.[role]?.[moduleName]?.[action]}
                          onChange={() => togglePermission(role, moduleName, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
};

export default RolePermissionsPage;
